/**
 * AllDayRow Component
 * Sticky row at the top of the calendar grid that displays day headers
 * and all-day events, similar to Google Calendar's combined header.
 */

import { format, isBefore, isSameDay, startOfDay } from 'date-fns';
import { Globe } from 'lucide-react';
import type { CalendarEventDto } from '@signalvote/shared';
import { cn } from '@/lib/utils';
import { getCampaignToday, isToday } from '@/lib/calendarUtils';
import type { KeyDateItem } from '@/lib/keyDates';
import { KeyDateChip } from './shared/KeyDateChip';

interface AllDayRowProps {
  visibleDays: Date[];
  allDayEventsByDate: Map<string, CalendarEventDto[]>;
  keyDatesByDate: Map<string, KeyDateItem[]>;
  onEventClick?: (event: CalendarEventDto) => void;
  /** The currently selected date (for visual highlighting) */
  selectedDate?: Date;
}

/**
 * Combined sticky header: day names + all-day events.
 * Uses CSS subgrid to align with the parent calendar grid columns.
 */
export function AllDayRow({ visibleDays, allDayEventsByDate, keyDatesByDate, onEventClick, selectedDate }: AllDayRowProps) {
  const hasAnyAllDayEvents =
    allDayEventsByDate.size > 0 || visibleDays.some((date) => keyDatesByDate.has(format(date, 'yyyy-MM-dd')));

  return (
    <div
      className="sticky top-0 z-[3] grid border-b border-calendar-grid bg-white"
      style={{
        gridColumn: '1 / -1',
        gridRow: 1,
        gridTemplateColumns: 'subgrid',
      }}
    >
      {/* Time gutter cell */}
      <div className="border-r border-dotted border-gray-200" />

      {/* Day columns */}
      {visibleDays.map((date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const events = allDayEventsByDate.get(dateStr) || [];
        const keyDates = keyDatesByDate.get(dateStr) || [];
        const today = isToday(date);
        const past = !today && isBefore(startOfDay(date), getCampaignToday());
        const selected = selectedDate ? isSameDay(date, selectedDate) : false;
        const selectedNotToday = selected && !today;

        return (
          <div
            key={dateStr}
            data-testid={`all-day-${dateStr}`}
            className={cn(
              'border-r border-dotted border-gray-200 min-w-0 overflow-hidden',
              today && 'bg-blue-50/20',
              selectedNotToday && 'bg-primary/5'
            )}
          >
            {/* Day header - Google Cal style: today gets blue circle on date number */}
            <div className="py-1.5 px-1 text-center">
              {today ? (
                <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                  {format(date, 'EEE')}{' '}
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold">
                    {format(date, 'd')}
                  </span>
                </span>
              ) : (
                <span
                  className={cn(
                    'text-sm',
                    past ? 'font-medium text-gray-400' : selectedNotToday ? 'font-bold text-primary' : 'font-medium text-gray-900'
                  )}
                >
                  {format(date, 'EEE d')}
                </span>
              )}
            </div>

            {/* All-day events */}
            {hasAnyAllDayEvents && (
              <div className="px-0.5 pb-1">
                {keyDates.length > 0 && (
                  <div role="list" className="flex flex-col gap-0.5 mb-0.5">
                    {keyDates.map((item) => (
                      <KeyDateChip key={item.key} item={item} className="text-[0.65rem]" />
                    ))}
                  </div>
                )}
                {events.length > 0 ? (
                  <div className={cn(past && 'opacity-40')}>
                    {events.map((evt) => (
                      <AllDayEventChip key={evt.id} event={evt} onClick={onEventClick} />
                    ))}
                  </div>
                ) : keyDates.length > 0 ? null : (
                  // Spacer so the row height stays consistent across days
                  <div className="h-1" />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface AllDayEventChipProps {
  event: CalendarEventDto;
  onClick?: (event: CalendarEventDto) => void;
}

function AllDayEventChip({ event, onClick }: AllDayEventChipProps) {
  const color = event.calendarColor || '#6366f1';
  const isOtherCampaign = event.isOtherCampaign ?? false;
  const isShowBusy = isOtherCampaign && event.visibility === 'ShowBusy';
  const displayTitle = isShowBusy ? 'Busy' : event.title;
  const badge = isOtherCampaign && !isShowBusy && event.sourceCampaignName
    ? `${event.sourceCampaignName}: `
    : '';

  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded px-1.5 py-0.5 mb-0.5 text-[0.65rem] leading-tight truncate',
        isOtherCampaign ? 'cursor-default pointer-events-none' : (onClick ? 'cursor-pointer hover:opacity-90' : 'cursor-default'),
        'transition-opacity',
        isShowBusy ? 'opacity-25' : isOtherCampaign ? 'opacity-35' : ''
      )}
      style={{
        backgroundColor: `${color}20`,
        borderLeft: `3px solid ${color}`,
        color: color,
      }}
      title={displayTitle}
      onClick={() => { if (!isOtherCampaign) onClick?.(event); }}
    >
      <Globe className="h-2.5 w-2.5 shrink-0" />
      <span className="truncate font-medium">{badge}{displayTitle}</span>
    </div>
  );
}

export default AllDayRow;
